import { HttpEvent } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

import { FormControl } from "@angular/forms";
import { InspectionImagesCount } from "../../_objects/inspection";
import {
  ImagesEvaluationList,
  InspectionImage,
  InspectionImageCreate,
  InspectionImageListItem,
  InspectionImageMultipleUpdate,
  InspectionImageOrderChange,
  InspectionImageUpdate,
  MeasurementInfo,
  ModifiedImageUseUpdate,
  ReadImageOption
} from "../../_objects/inspection-image";
import { TenantDataFile } from "../../_objects/tenant-data";
import { ApiService } from "../base/api-base.service";
import { InspectionImageContractService } from "../interfaces/inspection-image-contract.service";
import { NetworkStatusService } from "../network-status.service";
import { InspectionImageOfflineService } from "../offline/inspection-image-offline.service";
import { InspectionImageOnlineService } from "../online/inspection-image-online.service";
import { CreateImagesResponse } from "../responses/inspection-image.response";
import { InspectionImageFilters } from "../../_objects/inspection-image-filters";

@Injectable()
export class InspectionImageService extends ApiService<InspectionImageContractService> implements InspectionImageContractService {
  constructor(
    protected networkStatusService: NetworkStatusService,
    protected offlineService: InspectionImageOfflineService,
    protected onlineService: InspectionImageOnlineService
  ) {
    super(networkStatusService, offlineService, onlineService);
  }

  // PUT /api/v1/inspections/{inspectionId}/images/{inspectionImageId}/file
  updateInspectionImageFile(inspectionId: string, inspectionImageId: string, file: File): Observable<void> {
    return this.service.updateInspectionImageFile(inspectionId, inspectionImageId, file);
  }

  // IM01 GET /api/v1/inspections/{inspectionId}/images
  getInspectionImages(inspectionId: string, filters: InspectionImageFilters): Observable<InspectionImageListItem[]> {
    return this.service.getInspectionImages(inspectionId, filters);
  }

  // IM02 GET /api/v1/inspections/{inspectionId}/images/{inspectionImageId}
  getInspectionImage(inspectionId: string, inspectionImageId: string): Observable<InspectionImage> {
    return this.service.getInspectionImage(inspectionId, inspectionImageId);
  }

  // IM03 POST /api/v1/inspections/{inspectionId}/images
  fileUploadRaw(
    inspectionImageCreate: InspectionImageCreate,
    file: File,
    uploadPath: string,
    audioFile?: File,
    mviqComment?: string,
    mviqTagFlagIsAvailable?: boolean,
    isMviqImage?: boolean
  ): Observable<HttpEvent<{}>> {
    return this.service.fileUploadRaw(inspectionImageCreate, file, uploadPath, audioFile, mviqComment, mviqTagFlagIsAvailable, isMviqImage);
  }

  // IM04 PUT /api/v1/inspections/{inspectionId}/images/{inspectionImageId}
  patchInspectionImage(inspectionImageUpdate: InspectionImageUpdate): Observable<any> {
    return this.service.patchInspectionImage(inspectionImageUpdate);
  }

  // IM05 PUT /api/v1/inspections/{inspectionId}/images
  patchInspectionImageMultiple(inspectionId: string, inspectionImageMultipleUpdate: InspectionImageMultipleUpdate): Observable<any> {
    return this.service.patchInspectionImageMultiple(inspectionId, inspectionImageMultipleUpdate);
  }

  // IM06 DELETE /api/v1/inspections/{inspectionId}/images/{inspectionImageId}
  deleteInspectionImage(inspectionId: string, inspectionImageId: string): Observable<any> {
    return this.service.deleteInspectionImage(inspectionId, inspectionImageId);
  }

  // IM07 DELETE /api/v1/inspections/{inspectionId}/images
  deleteInspectionImageMultiple(inspectionId: string, inspectionImageIdDeleteList: string[]): Observable<any> {
    return this.service.deleteInspectionImageMultiple(inspectionId, inspectionImageIdDeleteList);
  }

  // IM08 PUT /api/v1/inspections/{inspectionId}/images/sortOrder
  patchInspectionImageOrderChange(inspectionId: string, imageOrders: InspectionImageOrderChange): Observable<InspectionImageListItem[]> {
    return this.service.patchInspectionImageOrderChange(inspectionId, imageOrders);
  }

  // IM09 PUT /api/v1/inspections/{inspectionId}/images/{inspectionImageId}/usedFlag
  putInspImageModifiedUse(modifiedUse: ModifiedImageUseUpdate): Observable<InspectionImage> {
    return this.service.putInspImageModifiedUse(modifiedUse);
  }

  // IM10 /api/image/file
  async getImageBinaryUrl(inspectionId: string, inspectionImageId: string, imageMode: number): Promise<string> {
    return this.service.getImageBinaryUrl(inspectionId, inspectionImageId, imageMode);
  }

  // /api/inspections/{inspectionId}/createImages
  createInspectionImages(inspectionId: string, inspectionImage: InspectionImageCreate[]): Observable<CreateImagesResponse> {
    return this.service.createInspectionImages(inspectionId, inspectionImage);
  }

  // IM12 /api/inspections/{inspectionId}/images/{inspectionImageId}/read
  readInspectionImage(inspectionId: string, inspectionImageId: string, readImageOption: ReadImageOption): Observable<InspectionImage> {
    return this.service.readInspectionImage(inspectionId, inspectionImageId, readImageOption);
  }

  // IM13 /api/inspections/{inspectionId}/images/{inspectionImageId}/measurementdata
  getInspectionImageMeasurementData(inspectionId: string, inspectionImageId: string): Observable<MeasurementInfo> {
    return this.service.getInspectionImageMeasurementData(inspectionId, inspectionImageId);
  }

  // IM14 GET /api/inspections/{inspectionId}/images/count/total
  getImageCountOfInspection(inspectionId: string): Observable<InspectionImagesCount> {
    return this.service.getImageCountOfInspection(inspectionId);
  }

  // IM14 POST /api/inspections/images/count
  getImageCountForInspections(inspectionIds: string[]): Observable<InspectionImagesCount[]> {
    return this.service.getImageCountForInspections(inspectionIds);
  }

  // ----------------------------------------

  convertInspectionImage2InpsctionImageUpdate(inspectionImage: InspectionImage): InspectionImageUpdate {
    const inspectionImageUpdate = new InspectionImageUpdate();
    inspectionImageUpdate.id = inspectionImage.id;
    inspectionImageUpdate.inspectionId = inspectionImage.inspectionId;
    inspectionImageUpdate.subjectLocationId = inspectionImage.subjectLocationId;
    inspectionImageUpdate.imageDescription = inspectionImage.imageDescription;
    inspectionImageUpdate.comment = inspectionImage.comment;
    inspectionImageUpdate.metadata = inspectionImage.metadata;
    inspectionImageUpdate.schemaVersion = inspectionImage.schemaVersion;
    inspectionImageUpdate.bookMarkOption = inspectionImage?.bookMarkOption;

    return inspectionImageUpdate;
  }

  convertInspectionImage2InspectionImageListItem(inspectionImage: InspectionImage): InspectionImageListItem {
    const inspectionImageListItem = new InspectionImageListItem();
    inspectionImageListItem.id = inspectionImage.id;
    inspectionImageListItem.inspectionId = inspectionImage.inspectionId;
    inspectionImageListItem.imageDescription = inspectionImage.imageDescription;
    inspectionImageListItem.comment = inspectionImage.comment;
    inspectionImageListItem.datetime = inspectionImage.datetime;
    inspectionImageListItem.fileName = inspectionImage.fileName;
    inspectionImageListItem.mimeType = inspectionImage.mimeType;
    inspectionImageListItem.subjectLocationId = inspectionImage.subjectLocationId;
    inspectionImageListItem.title = inspectionImage.metadata.title;
    inspectionImageListItem.tags = inspectionImage.metadata.tags;
    inspectionImageListItem.mark = inspectionImage.metadata.mark;
    inspectionImageListItem.thumbFilePath = inspectionImage.thumbFilePath;

    inspectionImageListItem.hasModifiedImage = inspectionImage.hasModifiedImage;
    inspectionImageListItem.hasMeasurementImage = inspectionImage.hasMeasurementImage;
    inspectionImageListItem.isMeasurementEnabled = inspectionImage.isMeasurementEnabled;
    inspectionImageListItem.usedImageMode = inspectionImage.usedImageMode;
    inspectionImageListItem.isDataPlate = inspectionImage.isDataPlate;
    inspectionImageListItem.orientation = inspectionImage.orientation;
    inspectionImageListItem.sortOrder = inspectionImage.sortOrder;
    inspectionImageListItem.permission = inspectionImage.permission;
    inspectionImageListItem.status = inspectionImage.status;
    inspectionImageListItem.usedImageMode = inspectionImage.usedImageMode;
    inspectionImageListItem.usedImageModeControl = new FormControl<number>(inspectionImage.usedImageMode);
    inspectionImageListItem.bookMarkOption = inspectionImage?.bookMarkOption;

    return inspectionImageListItem;
  }
}
